import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {HelpArticleResult, HelpStatus} from '@shared/models/contextual-help.model';
import {
  ApiHelpProvider,
  HelpContentProvider
} from '@shared/services/contextual-help/help-content.provider';

const DEFAULT_LOCALE = 'en-CA';

@Injectable({providedIn: 'root'})
export class ContextualHelpService {
  private readonly articleCache = new Map<string, Observable<HelpArticleResult>>();

  constructor(
    private provider: ApiHelpProvider,
    private transloco: TranslocoService
  ) {
    // Help follows the application language. Cached articles are locale-scoped, but the cache is
    // cleared on every switch so a language change never serves stale content.
    this.transloco.langChanges$.subscribe(() => this.clearClientCache());
  }

  /**
   * Resolves help for an identifier using Harmony's current UI language.
   * Callers never pass a language: the locale comes from the existing localization service, so
   * one identifier serves every locale.
   */
  getArticle(identifier: string): Observable<HelpArticleResult> {
    const key = (identifier || '').trim();
    if (!key) {
      return of({state: 'missing', messageKey: 'contextualHelp.missing'} as HelpArticleResult);
    }

    const locale = this.resolveLocale();
    const cacheKey = `${key}|${locale}`;
    const cached = this.articleCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = (this.provider as HelpContentProvider).getArticle(key, locale).pipe(
      map(article => ({state: 'found', article} as HelpArticleResult)),
      catchError((error: unknown) => of(this.mapError(error))),
      shareReplay({bufferSize: 1, refCount: false})
    );

    this.articleCache.set(cacheKey, request$);
    return request$;
  }

  getStatus(): Observable<HelpStatus> {
    return (this.provider as HelpContentProvider).getStatus().pipe(
      catchError(() => of({
        loaded: false,
        articleCount: 0,
        lastLoadError: 'Help status unavailable.'
      } as HelpStatus))
    );
  }

  clearClientCache(): void {
    this.articleCache.clear();
  }

  /** Current Harmony UI locale (BCP-47), e.g. en-CA / fr-CA. */
  resolveLocale(): string {
    return this.transloco.getActiveLang() || DEFAULT_LOCALE;
  }

  private mapError(error: unknown): HelpArticleResult {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
        return {state: 'missing', messageKey: 'contextualHelp.missing'};
      }
      if (error.status === 503) {
        return {state: 'unavailable', messageKey: 'contextualHelp.unavailable'};
      }
    }

    return {state: 'error', messageKey: 'contextualHelp.error'};
  }
}
